// What each report is, at which grain, and where to get it.
//
// Reports and Download were the same seven things split by verb: one page to
// look, another to get, and every report panel ending with "Export from the
// Download tab" — the page pointing elsewhere for the button that belongs on
// it.
//
// The worse half was not the split but the ambiguity it hid. The Download page
// listed the four categories TWICE, under two group headings, at two different
// grains with identical labels. On run 34:
//
//     same label            by deductor    by transaction
//     all_match                      43             6,732
//     in_26as_not_books              79            12,481
//     in_books_not_26as              62            44,956
//     detailed                      301            64,169
//
// Both are legitimate — a partner wants the 62 deductors, a preparer wants the
// 44,956 entries behind them — so neither is removed. They are named, the count
// travels on the button, and the two endpoints can no longer be crossed,
// because nothing else on the screen would tell you they had been.
import { apiUrl } from './client'
import type { DifferenceSummary, ReportsSummary } from './types'

/** `deductor` = one row per party (`_party_export_rows`).
 *  `transaction` = one row per entry (`DifferenceRowDB`). */
export type Grain = 'deductor' | 'transaction'

/** The four reports that exist at both grains, in the order the page shows
 *  them. Each maps to the difference bucket that holds its entries; `detailed`
 *  filters nothing, so it is every bucket. */
const CATEGORY_TABS: Record<string, string | null> = {
  all_match: 'ALL_MATCH',
  in_26as_not_books: 'ONLY_26AS',
  in_books_not_26as: 'ONLY_BOOKS',
  detailed: null,
}

/** Already aggregations, so a grain toggle would be meaningless on them —
 *  a party-wise pivot is party-wise by construction. */
const PIVOT_TABS = ['party', 'section', 'month', 'quarter', 'tan']

/** The two STATEMENTS (Stage C of the four-consumer plan): grainless
 *  label/amount walks for readers OUTSIDE the reconciliation — the return
 *  preparer (bridge) and the auditor (substantiation). One combined
 *  workbook serves both downloads. */
export const STATEMENT_TABS = ['bridge', 'substantiation']

export const REPORT_TABS = [...Object.keys(CATEGORY_TABS), ...PIVOT_TABS,
  ...STATEMENT_TABS]

const PIVOT_LABEL: Record<string, string> = {
  party: 'TDS difference — party-wise',
  section: 'TDS difference — section-wise',
  month: 'TDS difference — month-wise',
  quarter: 'TDS difference — quarter-wise',
  tan: 'TDS difference — TAN-wise',
}

const STATEMENT_LABEL: Record<string, string> = {
  bridge: 'Claimable-TDS bridge',
  substantiation: 'TDS receivable substantiation',
}

export function grainsOf(tab: string): Grain[] {
  return tab in CATEGORY_TABS ? ['deductor', 'transaction'] : []
}

export interface ExportLink {
  format: 'xlsx' | 'csv'
  /** the path alone, so a test can assert which endpoint is being asked
   *  without knowing the API base */
  path: string
  href: string
}

/** The two formats for one report at one grain.
 *
 *  `grain` is null for a pivot, which has one form only. The endpoint is
 *  chosen HERE rather than at the call site: these two answer the same question
 *  about the same category and differ only in grain, so a crossed link would
 *  hand over 44,956 rows under a button labelled 62 and nothing on the page
 *  would contradict it.
 */
export function exportLinks(
  runId: string,
  tab: string,
  grain: Grain | null,
): ExportLink[] {
  const isCategory = tab in CATEGORY_TABS
  const isPivot = PIVOT_TABS.includes(tab)
  const isStatement = STATEMENT_TABS.includes(tab)
  if (!isCategory && !isPivot && !isStatement) return []
  if (isCategory && grain == null) return []
  if ((isPivot || isStatement) && grain != null) return []

  const path = (format: string) =>
    (isStatement
      ? `/runs/${runId}/bridge/export?format=${format}`
      : grain === 'deductor'
        ? `/runs/${runId}/reports/${tab}/export?format=${format}`
        : `/runs/${runId}/difference/export?report=${tab}&format=${format}`)

  return (['xlsx', 'csv'] as const).map((format) => ({
    format,
    path: path(format),
    href: apiUrl(path(format)),
  }))
}

/** How many ENTRIES a category report covers, from the difference summary.
 *
 *  Null for a pivot and for anything unknown — "no answer" rather than a zero,
 *  because a zero on a button is a claim that there is nothing to download.
 */
export function entryCount(
  diff: DifferenceSummary | undefined,
  tab: string,
): number | null {
  if (!diff || !(tab in CATEGORY_TABS)) return null
  const status = CATEGORY_TABS[tab]
  if (status == null) return diff.buckets.reduce((a, b) => a + b.n, 0)
  return diff.buckets.find((b) => b.status === status)?.n ?? 0
}

export interface InventoryGrain {
  grain: Grain | null
  label: string
  count: number | null
  links: ExportLink[]
}

export interface InventoryRow {
  tab: string
  label: string
  isPivot: boolean
  grains: InventoryGrain[]
}

const GRAIN_LABEL: Record<Grain, string> = {
  deductor: 'By deductor',
  transaction: 'By transaction',
}

/** Every report, once, with both counts beside their own grain.
 *
 *  The whole inventory as data so the page renders it rather than restating
 *  it — the previous arrangement restated it twice and the two statements
 *  disagreed by a factor of 725.
 */
export function reportInventory(
  runId: string,
  summary: ReportsSummary | undefined,
  diff: DifferenceSummary | undefined,
): InventoryRow[] {
  return REPORT_TABS.map((tab) => {
    const isPivot = PIVOT_TABS.includes(tab)
    const isStatement = STATEMENT_TABS.includes(tab)
    const grains: InventoryGrain[] = (isPivot || isStatement)
      ? [{ grain: null, label: 'Download', count: null, links: exportLinks(runId, tab, null) }]
      : grainsOf(tab).map((g) => ({
        grain: g,
        label: GRAIN_LABEL[g],
        count: g === 'deductor'
          ? (summary?.tabs[tab]?.count ?? null)
          : entryCount(diff, tab),
        links: exportLinks(runId, tab, g),
      }))
    return {
      tab,
      label: isStatement ? STATEMENT_LABEL[tab]
        : isPivot ? PIVOT_LABEL[tab] : (summary?.tabs[tab]?.label ?? tab),
      isPivot: isPivot || isStatement,
      grains,
    }
  })
}
